import { useMemo, useContext } from 'react';

import { TeamsPageContext } from '@vyce/core/src/contexts';
import { usePermissions } from '@vyce/core/src/hooks/usePermissions';

import { useActions, useTypedSelector, useInviteButton } from 'src/hooks';
import { WidgetModule } from 'src/modules';
import { ChatUser, INTERFACE_PERMISSIONS } from '@vyce/core/src/types';
import { getStaffRequest } from '@vyce/core/src/api/companies';

export const useTeamsDashboardData = () => {
  const { dashboards, uuid, first_name, last_name, email, phone, photo } = useTypedSelector(
    state => state.user
  );
  const { selectedCompanyAppData, selectedCompany, mainDashboardFilters } = useTypedSelector(
    state => state.helper
  );
  const { loading, teams, selectedCompanyId = '' } = useContext(TeamsPageContext);
  const {
    isInviteFormOpen,
    subscribeLoading,
    isActivateSubscriptionDialogOpen,
    simpleSubscribe,
    setCloseInviteForm,
    handleInviteButtonClick,
    closeActivateSubscriptionDialog,
  } = useInviteButton();
  const { can } = usePermissions();
  const { updateUserDashboards, updateMainDashboardFilters } = useActions();
  const chatUser: ChatUser | undefined = {
    uid: uuid ?? '',
    name: `${first_name ?? ''} ${last_name ?? ''}`.trim(),
    email,
    phone,
    avatarUrl: photo?.url,
  };

  const hasTimePermissions = can(INTERFACE_PERMISSIONS.TIME);
  const hasTeamPermissions = can(INTERFACE_PERMISSIONS.TEAMS);

  const contextOptions = useMemo(
    () => ({
      dashboards,
      first_name,
      companyDataLoading: loading,
      teams: teams ?? [],
      selectedCompanyId,
      mainDashboardFilters,
      timePermission: !!hasTimePermissions,
      teamPermission: !!hasTeamPermissions,
      updateMainDashboardFilters,
      updateUserDashboards,
      widgetComponent: WidgetModule,
      onOpenUniversalInviteForm: handleInviteButtonClick,
      chatUser,
      reports: {
        clientApp: 'work' as const,
        currentUserId: uuid,
        companyName: selectedCompany?.name,
        getStaff: getStaffRequest,
      },
    }),
    [
      dashboards,
      first_name,
      selectedCompanyAppData,
      selectedCompanyId,
      loading,
      teams,
      mainDashboardFilters,
      hasTeamPermissions,
      hasTimePermissions,
      updateMainDashboardFilters,
      updateUserDashboards,
      chatUser,
      uuid,
      selectedCompany?.name,
    ]
  );
  return {
    isActivateSubscriptionDialogOpen,
    subscribeLoading,
    contextOptions,
    isInviteFormOpen,
    selectedCompany,
    simpleSubscribe,
    setCloseInviteForm,
    closeActivateSubscriptionDialog,
  };
};
